import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import useLivePoll from "@/hooks/useLivePoll";
import {
  LayoutDashboard, FlaskConical, Layers, Send, ScrollText, Users, Upload, LogOut, Menu, X, Microscope, UserSearch, BarChart3, ClipboardList, Scissors, LayoutGrid, ClipboardCheck, Stethoscope, Home, Slice, PlugZap, Keyboard, ChevronDown, Sparkles, ScanLine, Mail, ScanText, Activity,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import SilasListener from "@/components/SilasListener";
import ThemeToggle from "@/components/ThemeToggle";

const NAV_GROUPS = [
  {
    key: "worklists", label: "Worklists", icon: LayoutDashboard, testid: "nav-group-worklists",
    items: [
      { to: "/dashboard", label: "Lab Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/my-worklist", label: "My Worklist", icon: Stethoscope, testid: "nav-my-worklist", badge: "worklist", perm: "signout" },
      { to: "/tech-worklist", label: "Bench Worklist", icon: Slice, testid: "nav-tech-worklist", badge: "bench", perm: "inventory:write" },
    ],
  },
  {
    key: "cases", label: "Cases", icon: FlaskConical, testid: "nav-group-cases",
    items: [
      { to: "/cases", label: "All Cases", icon: FlaskConical, testid: "nav-cases" },
      { to: "/patients", label: "Patient History", icon: UserSearch, testid: "nav-patients" },
      { to: "/transcription", label: "Transcription", icon: Keyboard, testid: "nav-transcription", badge: "transcription", perm: "transcription:view" },
      { to: "/scan", label: "Scan Mode", icon: ScanLine, testid: "nav-scan-mode" },
      { to: "/assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-assistant", perm: "assistant" },
      { to: "/canned-texts", label: "Canned Texts", icon: Sparkles, testid: "nav-canned-texts" },
      { to: "/lablync", label: "LabLync", icon: Activity, testid: "nav-lablync", perm: "compliance:view" },
      { to: "/templates", label: "Templates", icon: ClipboardCheck, testid: "nav-templates" },
    ],
  },
  {
    key: "specimens", label: "Specimens", icon: Layers, testid: "nav-group-specimens",
    items: [
      { to: "/inventory", label: "Blocks & Slides", icon: Layers, testid: "nav-inventory" },
      { to: "/histology-requests", label: "Histology Requests", icon: Scissors, testid: "nav-histology-requests", badge: "recuts" },
      { to: "/bulk-slides", label: "Bulk Slides", icon: LayoutGrid, testid: "nav-bulk-slides" },
    ],
  },
  {
    key: "sendouts", label: "Send-Outs", icon: Send, testid: "nav-group-sendouts",
    items: [
      { to: "/sendouts", label: "Send-Outs & Billing", icon: Send, testid: "nav-sendouts" },
      { to: "/worklist", label: "Billing Worklist", icon: ClipboardList, testid: "nav-worklist" },
    ],
  },
  {
    key: "quality", label: "Quality", icon: BarChart3, testid: "nav-group-quality",
    items: [
      { to: "/statistics", label: "Statistics", icon: BarChart3, testid: "nav-statistics", perm: "stats:view" },
      { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", perm: "audit:view" },
    ],
  },
  {
    key: "admin", label: "Setup & IT", icon: Users, testid: "nav-group-admin",
    items: [
      { to: "/team", label: "Team & Lab", icon: Users, testid: "nav-team", perm: "team:manage" },
      { to: "/import", label: "Import Data", icon: Upload, testid: "nav-import", perm: "import" },
      { to: "/interop", label: "EHR Interop", icon: PlugZap, testid: "nav-interop" },
      { to: "/archive", label: "ArchivIQ-Med", icon: ScanText, testid: "nav-archive" },
      { to: "/demo-requests", label: "Demo Requests", icon: Mail, testid: "nav-demo-requests", perm: "team:manage" },
    ],
  },
];

const HUB_ITEM = { to: "/hub", label: "Modules", icon: Home, testid: "nav-hub" };
const ASSISTANT_ITEM = { to: "/assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-assistant-top" };

export const Layout = ({ children, bare = false }) => {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const activePath = useLocation().pathname;
  const [menu, setMenu] = useState(null);
  const closer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pendingRecuts, setPendingRecuts] = useState(0);
  const [readyCases, setReadyCases] = useState(0);
  const [benchCases, setBenchCases] = useState(0);
  const [readyReports, setReadyReports] = useState(0);
  const [labLogo, setLabLogo] = useState(null);
  const [labName, setLabName] = useState("");
  const [brandLogoOk, setBrandLogoOk] = useState(false);

  useEffect(() => {
    api.get("/lab").then((r) => {
      setLabName(r.data.lab_name || "");
      if (r.data.has_logo) {
        api.get("/lab/logo", { responseType: "blob" })
          .then((res) => setLabLogo(URL.createObjectURL(res.data))).catch(() => {});
      } else {
        setLabLogo(null);
      }
    }).catch(() => {});
  }, []);

  const loadRecuts = async () => {
    const { data } = await api.get("/histology-requests/summary");
    setPendingRecuts(data.outstanding || 0);
  };
  const loadWorklist = async () => {
    const { data } = await api.get("/worklist/summary");
    setReadyCases(data.ready || 0);
  };
  const loadBench = async () => {
    const { data } = await api.get("/tech-worklist/summary");
    setBenchCases(data.outstanding || 0);
  };
  const loadTranscription = async () => {
    const { data } = await api.get("/transcription/summary");
    setReadyReports(data.ready || 0);
  };
  useEffect(() => { loadRecuts().catch(() => {}); }, []);
  useEffect(() => { if (can("transcription:view")) loadTranscription().catch(() => {}); }, [can]);
  useEffect(() => { if (can("inventory:write")) loadBench().catch(() => {}); }, [can]);
  useEffect(() => { if (can("signout")) loadWorklist().catch(() => {}); }, [can]);
  useLivePoll(() => {
    loadRecuts().catch(() => {});
    if (can("signout")) loadWorklist().catch(() => {});
    if (can("inventory:write")) loadBench().catch(() => {});
    if (can("transcription:view")) loadTranscription().catch(() => {});
  }, 10000);

  const badgeCount = (badge) => ({
    recuts: pendingRecuts, worklist: readyCases, bench: benchCases, transcription: readyReports,
  }[badge] || 0);
  const badgeTone = (badge) => (badge === "recuts" || badge === "transcription"
    ? "bg-amber-500 text-white" : "bg-teal-300 text-[#134e4a]");

  const groups = bare ? [] : NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((n) => !n.perm || can(n.perm)) }))
    .filter((g) => g.items.length > 0);
  const groupCount = (g) => g.items.reduce((sum, n) => sum + badgeCount(n.badge), 0);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-[#134e4a] no-print">
        <div className="flex h-14 items-center gap-3 px-3 sm:px-5">
          <button
            className={`${bare ? "hidden" : "lg:hidden"} inline-flex h-11 w-11 items-center justify-center rounded-md text-white/90 hover:bg-white/10`}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle navigation"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <NavLink to="/hub" className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="header-brand" title="TraX Suite by PathQity">
            <span className={`inline-flex items-center rounded bg-white px-2 py-1 shadow-sm ${brandLogoOk ? "" : "hidden"}`}>
              <img src="/pathqity-logo.png" alt="PathQity"
                onLoad={() => setBrandLogoOk(true)} onError={() => setBrandLogoOk(false)}
                className="h-5 w-auto max-w-[120px] object-contain sm:h-7 sm:max-w-[170px]"
                data-testid="header-pathqity-logo" />
            </span>
            {!brandLogoOk && (
              <div className="flex items-center gap-2" data-testid="header-wordmark">
                <Microscope size={20} className="text-teal-200" />
                <span className="font-display text-lg font-bold tracking-tight text-white">PathQity</span>
              </div>
            )}
            {labLogo && (
              <>
                <span className="hidden h-7 w-px bg-white/25 sm:block" aria-hidden="true" />
                <span className="hidden items-center rounded bg-white px-2 py-1 shadow-sm sm:inline-flex">
                  <img src={labLogo} alt={labName || "Lab logo"} className="h-7 w-auto max-w-[170px] object-contain" data-testid="header-lab-logo" />
                </span>
              </>
            )}
          </NavLink>
          <nav className="ml-6 hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
            <NavLink to={HUB_ITEM.to} data-testid={HUB_ITEM.testid}
                     className={({ isActive }) =>
                       `rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-white text-[#134e4a]" : "text-teal-50/90 hover:bg-white/10"}`}>
              {HUB_ITEM.label}
            </NavLink>
            {can("assistant") && (
            <NavLink to={ASSISTANT_ITEM.to} data-testid={ASSISTANT_ITEM.testid}
                     className={({ isActive }) =>
                       `inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-white text-[#134e4a]" : "text-teal-50/90 hover:bg-white/10"}`}>
              <Sparkles size={14} /> {ASSISTANT_ITEM.label}
            </NavLink>
            )}
            {groups.map((g) => {
              const count = groupCount(g);
              const active = g.items.some((n) => n.to === activePath);
              return (
                <div key={g.key} className="relative"
                     onMouseEnter={() => { clearTimeout(closer.current); setMenu(g.key); }}
                     onMouseLeave={() => { closer.current = setTimeout(() => setMenu(null), 180); }}
                     onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setMenu(null); }}>
                  <button type="button" data-testid={g.testid}
                          onClick={() => setMenu(g.key)}
                          onFocus={() => setMenu(g.key)}
                          aria-expanded={menu === g.key}
                          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            active || menu === g.key ? "bg-white text-[#134e4a]" : "text-teal-50/90 hover:bg-white/10"}`}>
                    {g.label}
                    {count > 0 && (
                      <span data-testid={`${g.testid}-badge`}
                            className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${badgeTone(g.items.find((n) => badgeCount(n.badge) > 0)?.badge)}`}>
                        {count}
                      </span>
                    )}
                    <ChevronDown size={14} className={`transition-transform ${menu === g.key ? "rotate-180" : ""}`} />
                  </button>
                  {menu === g.key && (
                    <div data-testid={`${g.testid}-menu`}
                         className="absolute left-0 top-full z-50 w-60 border border-slate-200 bg-white pt-1 shadow-xl">
                      {g.items.map((n) => (
                        <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={() => setMenu(null)}
                                 className={({ isActive }) =>
                                   `flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                                     isActive ? "bg-[#134e4a]/10 text-[#134e4a]" : "text-slate-700 hover:bg-slate-50 hover:text-[#134e4a]"}`}>
                          <n.icon size={16} className="shrink-0 text-[#134e4a]" />
                          <span className="min-w-0 flex-1">{n.label}</span>
                          {badgeCount(n.badge) > 0 && (
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${badgeTone(n.badge)}`}
                                  data-testid={`nav-${n.badge}-badge`}>
                              {badgeCount(n.badge)}
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {!bare && <NotificationBell />}
          <ThemeToggle className="rounded-md p-2 text-white/80 hover:bg-white/10 hover:text-white" />
            <Link to="/account" data-testid="nav-account"
                  className="hidden text-right leading-tight sm:block hover:opacity-90">
              <div className="text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs uppercase tracking-wide text-teal-200" data-testid="current-user-role">{user?.role_label || user?.role}</div>
            </Link>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-white/25 px-3 text-sm font-medium text-white hover:bg-white/10"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {open && !bare && (
          <nav className="border-t border-white/15 px-2 pb-2 lg:hidden" data-testid="mobile-nav">
            <NavLink to={HUB_ITEM.to} onClick={() => setOpen(false)} data-testid={`${HUB_ITEM.testid}-mobile`}
                     className={({ isActive }) =>
                       `flex min-h-[48px] items-center gap-3 rounded-md px-3 text-base font-medium ${
                         isActive ? "bg-white text-[#134e4a]" : "text-teal-50/90"}`}>
              <Home size={18} /> {HUB_ITEM.label}
            </NavLink>
            {can("assistant") && (
            <NavLink to={ASSISTANT_ITEM.to} onClick={() => setOpen(false)} data-testid={`${ASSISTANT_ITEM.testid}-mobile`}
                     className={({ isActive }) =>
                       `mt-1 flex min-h-[48px] items-center gap-3 rounded-md px-3 text-base font-medium ${
                         isActive ? "bg-white text-[#134e4a]" : "text-teal-50/90"}`}>
              <Sparkles size={18} /> {ASSISTANT_ITEM.label}
            </NavLink>
            )}
            {groups.map((g) => (
              <div key={g.key} className="mt-1">
                <p className="px-3 pt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-teal-200">{g.label}</p>
                {g.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`${n.testid}-mobile`}
                    className={({ isActive }) =>
                      `flex min-h-[48px] items-center gap-3 rounded-md px-3 text-base font-medium ${
                        isActive ? "bg-white text-[#134e4a]" : "text-teal-50/90"}`}
                  >
                    <n.icon size={18} /> {n.label}
                    {badgeCount(n.badge) > 0 && (
                      <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${badgeTone(n.badge)}`}>
                        {badgeCount(n.badge)}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        )}
      </header>
      <main className="px-3 py-5 sm:px-6 sm:py-7">{children}</main>
      <SilasListener enabled={!!user && user.role !== "viewer"}
                     handsFree={!!user?.hands_free_enabled} />
    </div>
  );
};

export default Layout;
